import type { TableAction, TableActionGroup } from "@shared/types/data-table"
import type { PricingTable } from "@/features/subscriptions/types/pricing-table/pricing-table.types"
import { type BaseActionHandlers } from "./base-actions"

interface PricingTableSpecificHandlers {
  onViewPricingTable?: (table: PricingTable) => void
  onEditPricingTable?: (table: PricingTable) => void
  onDuplicatePricingTable?: (table: PricingTable) => void
  onArchivePricingTable?: (table: PricingTable) => void
  onPublishPricingTable?: (table: PricingTable) => void
  onGetEmbedCode?: (table: PricingTable) => void
  onPreviewPricingTable?: (table: PricingTable) => void
  onBulkArchive?: (ids: string[]) => void
  onBulkDuplicate?: (ids: string[]) => void
}

export type PricingTableActionHandlers = BaseActionHandlers<PricingTable> & PricingTableSpecificHandlers

const createPricingTableRowActions = (handlers: PricingTableActionHandlers): TableActionGroup<PricingTable>[] => [
  {
    label: "ACTIONS",
    actions: [
      {
        key: "previewPricingTable",
        label: "Preview",
        onClick: (table: PricingTable) => handlers.onPreviewPricingTable?.(table),
      },
      {
        key: "editPricingTable",
        label: "Edit",
        onClick: (table: PricingTable) => handlers.onEditPricingTable?.(table),
      },
      {
        key: "getEmbedCode",
        label: "Get embed code",
        onClick: (table: PricingTable) => handlers.onGetEmbedCode?.(table),
      },
      {
        key: "duplicatePricingTable",
        label: "Duplicate",
        onClick: (table: PricingTable) => handlers.onDuplicatePricingTable?.(table),
      },
      {
        key: "publishPricingTable",
        label: "Publish",
        onClick: (table: PricingTable) => handlers.onPublishPricingTable?.(table),
        condition: (table: PricingTable) => table.status === 'draft',
      },
      {
        key: "archivePricingTable",
        label: "Archive",
        onClick: (table: PricingTable) => handlers.onArchivePricingTable?.(table),
        condition: (table: PricingTable) => table.status !== 'archived',
        variant: "destructive" as const,
      },
    ]
  },
]

export const getPricingTableRowActions = (handlers: PricingTableActionHandlers = {}): TableActionGroup<PricingTable>[] =>
  createPricingTableRowActions(handlers)

export const getPricingTableBulkActions = (handlers: PricingTableActionHandlers = {}) => [
  {
    key: "duplicate",
    label: "Duplicate",
    onClick: (ids: string[]) => handlers.onBulkDuplicate?.(ids),
    disabled: (ids: string[]) => ids.length === 0,
  },
  {
    key: "archive",
    label: "Archive",
    onClick: (ids: string[]) => handlers.onBulkArchive?.(ids),
    disabled: (ids: string[]) => ids.length === 0,
  },
]

export const getPricingTableHoverActions = (handlers: PricingTableActionHandlers = {}): TableAction<PricingTable>[] => [
  {
    key: "previewPricingTable",
    label: "Preview",
    onClick: (table: PricingTable) => handlers.onPreviewPricingTable?.(table),
  },
]

// Panel actions for detail pages
const createPricingTablePanelActions = (handlers: PricingTableActionHandlers): TableActionGroup<PricingTable>[] => [
  {
    label: "ACTIONS",
    actions: [
      {
        key: "editPricingTable",
        label: "Edit",
        onClick: (table: PricingTable) => handlers.onEditPricingTable?.(table),
      },
      {
        key: "getEmbedCode",
        label: "Get embed code",
        onClick: (table: PricingTable) => handlers.onGetEmbedCode?.(table),
      },
      {
        key: "duplicatePricingTable",
        label: "Duplicate",
        onClick: (table: PricingTable) => handlers.onDuplicatePricingTable?.(table),
      },
      {
        key: "publishPricingTable",
        label: "Publish",
        onClick: (table: PricingTable) => handlers.onPublishPricingTable?.(table),
        condition: (table: PricingTable) => table.status === 'draft',
      },
      {
        key: "archivePricingTable",
        label: "Archive",
        onClick: (table: PricingTable) => handlers.onArchivePricingTable?.(table),
        condition: (table: PricingTable) => table.status !== 'archived',
        variant: "destructive" as const,
      },
    ]
  },
]

export const getPricingTablePanelActions = (handlers: PricingTableActionHandlers = {}) =>
  createPricingTablePanelActions(handlers)
